/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on May 22, 2026 by hoarfroster
 *
 */

import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { User } from '@models/user/user.ts';
import httpRequest from '@utils/http-request.ts';
import { setCookie } from '@utils/cookie.ts';

const API_BASE = `${import.meta.env.VITE_SERVER_ENDPOINT}/user/user/passkey`;

interface WebAuthnBeginResponse<TPublicKey> {
    data: {
        publicKey: TPublicKey;
    };
}

type PasskeyRegistrationOptions = Parameters<typeof startRegistration>[0];
type PasskeyAuthenticationOptions = Parameters<typeof startAuthentication>[0];

export interface PasskeyJSON {
    id: string;
    transports?: string | null;
    registeredAt: number | null;
    lastUsedAt?: number | null;
    deviceName?: string | null;
    backupState: boolean;
}

interface PasskeyRegisterFinishResult {
    passkeyId: string;
}

interface PasskeyMutationResponse {
    data: PasskeyJSON;
    message?: string;
}

interface PasskeyAuthFinishResultJSON {
    token: string;
    user: Parameters<typeof User.fromJSON>[0];
}

async function getResponseMessage(response: Response, fallback: string): Promise<string> {
    try {
        const payload = (await response.clone().json()) as { message?: string };
        return payload.message ?? fallback;
    } catch {
        return fallback;
    }
}

export async function registerPasskey(deviceName?: string) {
    // Step 1: Get registration options from backend
    const resp = await httpRequest(`${API_BASE}/register/begin`, 'POST');
    if (!resp.ok) throw new Error(await getResponseMessage(resp, 'Failed to get registration options'));
    const payload = (await resp.json()) as WebAuthnBeginResponse<PasskeyRegistrationOptions>;
    const publicKeyOptions = payload.data.publicKey;

    // Step 2: Create credential in browser
    const attResp = await startRegistration(publicKeyOptions);
    const trimmedDeviceName = deviceName?.trim();
    let normalizedDeviceName: string | undefined;
    if (trimmedDeviceName) {
        normalizedDeviceName = trimmedDeviceName;
    }

    // Step 3: Send credential to backend
    const finishResp = await httpRequest(`${API_BASE}/register/finish`, 'POST', {
        ...attResp,
        deviceName: normalizedDeviceName,
    });
    if (!finishResp.ok) throw new Error(await getResponseMessage(finishResp, 'Failed to register passkey'));
    return ((await finishResp.json()) as { data: PasskeyRegisterFinishResult }).data;
}

export async function updatePasskeyDeviceName(id: string, deviceName: string): Promise<PasskeyJSON> {
    const response = await httpRequest(`${API_BASE}/${id}`, 'PATCH', { deviceName });
    if (!response.ok) throw new Error(await getResponseMessage(response, 'Failed to update passkey'));
    return ((await response.json()) as PasskeyMutationResponse).data;
}

export async function deletePasskey(id: string): Promise<void> {
    const response = await httpRequest(`${API_BASE}/${id}`, 'DELETE');
    if (!response.ok) throw new Error(await getResponseMessage(response, 'Failed to delete passkey'));
}

export async function authenticatePasskey(userName: string | undefined): Promise<{ token: string; user: User }> {
    const normalizedUserName = userName?.trim();
    const beginEndpoint = normalizedUserName
        ? `${API_BASE}/auth/${normalizedUserName}/begin`
        : `${API_BASE}/auth/begin`;
    const finishEndpoint = normalizedUserName
        ? `${API_BASE}/auth/${normalizedUserName}/finish`
        : `${API_BASE}/auth/finish`;
    // Step 1: Get authentication options from backend
    const resp = await httpRequest(beginEndpoint, 'POST');
    if (!resp.ok) throw new Error(await getResponseMessage(resp, 'Failed to get authentication options'));
    const payload = (await resp.json()) as WebAuthnBeginResponse<PasskeyAuthenticationOptions>;
    const publicKeyOptions = payload.data.publicKey;

    // Step 2: Browser verifies credential
    const assertionResp = await startAuthentication(publicKeyOptions);

    // Step 3: Send assertion to backend
    const finishResp = await httpRequest(finishEndpoint, 'POST', assertionResp);
    if (!finishResp.ok) throw new Error(await getResponseMessage(finishResp, 'Failed to authenticate passkey'));

    const result = (await finishResp.json()) as { data: PasskeyAuthFinishResultJSON };
    const authenticatedUser = User.fromJSON(result.data.user);

    // Save token and user info to localStorage
    localStorage.setItem('user-info', JSON.stringify(authenticatedUser));
    localStorage.setItem('token', result.data.token);
    setCookie('token', result.data.token);

    return {
        token: result.data.token,
        user: authenticatedUser,
    };
}
